import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { MapPin, Clock, Info, Search } from "lucide-react";
import { sessionsAPI } from "../services/api";
import { useApp } from "../contexts/AppContext";
import "./Programme.css";

const Programme = () => {
  const { eventId } = useParams();
  const { currentEvent } = useApp();
  const [activeDay, setActiveDay] = useState(1);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Scroll to top when switching days
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeDay]);

  useEffect(() => {
    if (eventId) {
      fetchSessions();
    }
  }, [activeDay, eventId]);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const response = await sessionsAPI.getByDay(activeDay, eventId);
      // Sort by start_time
      const sorted = (response.data || []).sort(
        (a, b) => new Date(a.start_time) - new Date(b.start_time),
      );
      setSessions(sorted);
    } catch (error) {
      console.error("Failed to fetch sessions:", error);
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const days = useMemo(() => {
    if (!currentEvent) return [];
    
    const start = new Date(currentEvent.start_date);
    const end = new Date(currentEvent.end_date);
    
    // Normalize to midnight for calculation
    const sDate = new Date(start).setHours(0,0,0,0);
    const eDate = new Date(end).setHours(0,0,0,0);
    const diffDays = Math.round(Math.abs(eDate - sDate) / (1000 * 60 * 60 * 24)) + 1;
    
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const fullDayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

    return Array.from({ length: Math.max(1, diffDays) }, (_, i) => {
      const date = new Date(sDate);
      date.setDate(date.getDate() + i);
      
      return {
        id: i + 1,
        label: `Day ${i + 1}`,
        date: `${dayNames[date.getDay()]}, ${date.getDate()} ${monthNames[date.getMonth()]}`,
        fullDate: `${fullDayNames[date.getDay()]}, ${date.getDate()} ${monthNames[date.getMonth()]} ${date.getFullYear()}`,
      };
    });
  }, [currentEvent]);

  const currentDayInfo = days.find((d) => d.id === activeDay);

  const formatDateRange = (startStr, endStr) => {
    if (!startStr) return "";
    const start = new Date(startStr);
    const end = endStr ? new Date(endStr) : null;
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    if (!end || start.toDateString() === end.toDateString()) {
      return `${start.getDate()} ${monthNames[start.getMonth()]} ${start.getFullYear()}`;
    }
    return `${start.getDate()}-${end.getDate()} ${monthNames[start.getMonth()]} ${start.getFullYear()}`;
  };

  // Helper to format time HH:mm
  const formatTime = (isoString) => {
    if (!isoString) return "";
    return new Date(isoString).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const filteredSessions = sessions.filter((session) => {
    const term = searchTerm.toLowerCase();
    const titleMatch = session.title?.toLowerCase().includes(term);
    const descMatch = session.description?.toLowerCase().includes(term);
    const speakerMatch = session.speakers?.some((s) =>
      s.name.toLowerCase().includes(term),
    );
    const locationMatch = session.location?.toLowerCase().includes(term);

    return titleMatch || descMatch || speakerMatch || locationMatch;
  });

  return (
    <div className={`programme-page ${days.length <= 1 ? 'no-tabs' : 'has-tabs'}`}>
      {/* Conference Header */}
      <div className="conference-header">
        <h1 className="conference-title">
          {currentEvent?.title || "Loading Conference..."}
        </h1>
        <p className="conference-subtitle">
          {currentEvent?.description}
        </p>
        <div className="conference-details">
          <div className="conference-detail-item">
            <span className="detail-value">
              {currentEvent ? formatDateRange(currentEvent.start_date, currentEvent.end_date) : "Loading dates..."}
            </span>●
            <span className="detail-value">{currentEvent?.location || "Loading venue..."}</span>
          </div>
        </div>
      </div>

      {/* Day Tabs */}
      {days.length > 1 && (
        <div className="day-tabs">
          {days.map((day) => (
            <button
              key={day.id}
              className={`day-tab ${activeDay === day.id ? "active" : ""}`}
              onClick={() => setActiveDay(day.id)}
            >
              <span className="day-label">{day.label}</span>
              <span className="day-date">{day.date}</span>
            </button>
          ))}
        </div>
      )}

      {/* Search Bar */}
      <div className="search-container">
        <div className="search-input-wrapper">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder="Search sessions, speakers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Timeline */}
      <div className="timeline-container">
        {loading ? (
          <div
            style={{ textAlign: "center", padding: "40px", color: "#64748b" }}
          >
            Loading schedule...
          </div>
        ) : filteredSessions.length === 0 ? (
          <div
            style={{ textAlign: "center", padding: "40px", color: "#64748b" }}
          >
            {searchTerm
              ? "No sessions found matching your search."
              : "To be announced shortly"}
          </div>
        ) : (
          filteredSessions.map((item, index) => {
            // Map Session DB Object to UI fields
            const startTime = formatTime(item.start_time);
            const endTime = formatTime(item.end_time);
            const isBreak =
              item.session_type === "break" ||
              item.session_type === "networking";

            // Collect speakers names
            const speakerNames = item.speakers?.map((s) => s.name) || [];

            return (
              <div
                key={item.id || index}
                className={`timeline-item ${item.session_type === "break" ? "break" : ""}`}
              >
                {/* Time Column */}
                <div className="time-col">
                  <span className="time-start">{startTime}</span>
                  <span className="time-end">{endTime}</span>
                </div>

                {/* Content Card */}
                <div className="timeline-card">
                  {!isBreak && (
                    <span className={`card-tag ${item.session_type}`}>
                      {item.session_type}
                    </span>
                  )}

                  <h3 className="card-title">{item.title}</h3>

                  {/* Reuse description for subtitle/desc if short? 
                      Or just display description as desc */}
                  {item.description && (
                    <p className="card-desc" style={{ whiteSpace: "pre-line" }}>
                      {item.description}
                    </p>
                  )}

                  {/* Speakers / Moderators */}
                  {speakerNames.length > 0 && (
                    <div className="card-people">
                      <div className="people-group">
                        <span className="people-label">Speakers</span>
                        {speakerNames.length === 1 ? (
                          <div className="people-list">{speakerNames[0]}</div>
                        ) : (
                          <ul className="people-list">
                            {speakerNames.map((name, idx) => (
                              <li key={idx}>{name}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  )}

                  {item.location && item.location !== "NMAJS" && (
                    <div
                      style={{
                        marginTop: "12px",
                        fontSize: "0.85rem",
                        color: "#64748b",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <MapPin size={14} /> {item.location}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Programme;
